import { useEffect, useRef } from 'react';

export default function DocumentarySection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.doc-animate').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
                (el as HTMLElement).classList.remove('opacity-0', 'translate-y-8');
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="documentary" className="py-24 md:py-32 bg-gradient-to-b from-slate-50 to-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16 doc-animate opacity-0 translate-y-8 transition-all duration-700">
          <p className="text-sky-500 text-sm uppercase tracking-[0.25em] font-semibold mb-3">Film</p>
          <h2
            className="text-3xl md:text-4xl font-bold text-slate-800"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Documentary Experience
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Part 1 */}
          <div className="doc-animate opacity-0 translate-y-8 transition-all duration-700 rounded-2xl overflow-hidden border border-slate-100 bg-white hover:shadow-lg transition-shadow">
            {/* YouTube Embed */}
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe
                src="https://www.youtube.com/embed/q_MlFk_YUBg"
                title="Echoes of the Ganga — Part 1: The Reality"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
              {/* Part label */}
              <div className="absolute top-4 left-4 bg-sky-500 text-white text-xs font-bold px-3 py-1.5 rounded-full z-10 pointer-events-none">
                Part 1 — Live
              </div>
            </div>
            <div className="p-6">
              <h3
                className="text-slate-800 font-bold text-xl mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Echoes of the Ganga — Part 1: The Reality
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                This documentary explores pollution using data and real-world observations. Shot across the most sacred stretches of the Ganga, it reveals the ground reality behind the numbers.
              </p>
              <div className="flex items-center gap-2 text-sky-600">
                <i className="ri-map-pin-line text-sm" />
                <span className="text-sm font-medium">Shot across: Devprayag, Rishikesh, Haridwar</span>
              </div>
            </div>
          </div>

          {/* Part 2 */}
          <div className="doc-animate opacity-0 translate-y-8 transition-all duration-700 rounded-2xl overflow-hidden border border-slate-100 bg-white hover:shadow-lg transition-shadow">
            <div className="relative h-64 overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/89ccb77a23945f52a1fcb8a53182dd2f.jpg"
                alt="Documentary Part 2"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/20" />
              {/* Coming Soon overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/30 mb-3">
                  <i className="ri-lock-line text-white text-xl" />
                </div>
                <span className="text-white/80 text-sm font-medium">Coming Soon</span>
              </div>
              {/* Part label */}
              <div className="absolute top-4 left-4 bg-slate-600/80 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full">
                Part 2 — Coming Soon
              </div>
            </div>
            <div className="p-6">
              <h3
                className="text-slate-800 font-bold text-xl mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Echoes of the Ganga Part 2: The Path to Solutions
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                The journey continues beyond the problem exploring real solutions, ongoing efforts, and what data reveals about the future of the Ganga.
              </p>
              {/* Suspense line */}
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-compass-3-line text-sky-500 text-lg mt-0.5" />
                  <p className="text-sky-700 text-sm italic leading-relaxed">
                    We return to the same locations — this time to uncover solutions and real change.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
